import React, { useState } from 'react'
import PropTypes from 'prop-types'
import cloneDeep from 'lodash/cloneDeep'
import { toast } from 'react-toastify'

import { get } from 'utils/lodash'
import { handleError } from 'utils/error'
import { useAddPaymentMethodMutation } from 'api/payment-gateway/paymentGateway'
import { DEFAULT_AUTOPAY_TIMING, ACH_ACCOUNT_TYPE, PAYMENT_METHODS } from 'settings/constants/payment'

import T from 'T'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogTitle from 'components/common/HHDialogTitle'

import usePaymentProvider from 'components/customer-details/content/payment-details/usePaymentProvider'
import FortisElements from 'components/customer-details/content/payment-details/FortisElements'
import { DialogContent, Alert, Button, Box } from '@mui/material'
import AutopayDisclaimer from 'components/common/payments/AutopayDisclaimer'
import { CommonSwitch } from 'components/common'
import RenderPaymentForm from './RenderPaymentForm'

const { ACH } = PAYMENT_METHODS

const FORM_FIELDS = {
  accountToken: null,
  expiry: null,
  cardHolderName: '',
  cvv: '',
  accountType: ACH_ACCOUNT_TYPE[0],
  cardNickName: '',
  defaultAccount: false,
  autoPay: false,
  autoPayTime: DEFAULT_AUTOPAY_TIMING,
}

const NewPaymentMethodDialog = ({ isOpen = false, accountId, type, onClose }) => {
  const [addPaymentMethod, { isLoading }] = useAddPaymentMethodMutation()
  const { isFortis } = usePaymentProvider(accountId)
  const [newMethodFields, setNewMethodFields] = useState(cloneDeep(FORM_FIELDS))
  const [fortisError, setFortisError] = useState(null)
  const [fortisKey, setFortisKey] = useState(0)
  const { accountToken, expiry, cardHolderName, cardNickName, cvv, accountType, autoPay } = newMethodFields

  const handleChange = (key, value) => setNewMethodFields(method => ({ ...method, [key]: value }))

  const handleChangeBulk = value => setNewMethodFields(method => ({ ...method, ...value }))

  const resetFormFields = () => {
    setNewMethodFields(cloneDeep(FORM_FIELDS))
    setFortisError(null)
    setFortisKey(prev => prev + 1)
  }

  const handleClose = () => {
    resetFormFields()
    onClose()
  }

  const handleRetry = () => {
    setFortisError(null)
    setFortisKey(prev => prev + 1)
  }

  const handleCardConnectSave = () => {
    const payload = {
      accountId,
      accountToken,
      expiry,
      cardHolderName,
      cardNickName,
      defaultAccount: false,
      // accounType if ACH possible values Saving or Checking , for Credit Card send this as null
      accountType: type === ACH ? accountType : null,
      autoPayTime: autoPay ? DEFAULT_AUTOPAY_TIMING : null,
      cvv,
    }
    addPaymentMethod({ payload })
      .unwrap()
      .then(response => {
        const status = get(response, 'status')
        if (status === 'Added') {
          toast.success(T.PAYMENT_METHOD_ADD_SUCCESS_MESSAGE)
        } else if (status === 'Exists') {
          toast.error(T.PAYMENT_METHOD_ALREADY_EXISTS)
        } else {
          toast.error(status)
        }
        handleClose()
      })
      .catch(handleError)
  }

  const handleFortisSave = result => {
    // Only proceed when Fortis explicitly approves the tokenization
    const token = get(result, 'id', '') || get(result, 'data.id', '')
    if (!token) {
      return
    }

    const accountToken = get(result, 'data.id')
    const expiry = get(result, 'data.exp_date')
    const cardHolderName = get(result, 'data.account_holder_name')
    const cardNickName = get(result, 'data.account_holder_name')
    const isCC = get(result, 'data.payment_method') === 'cc'
    const accountTypeRaw = get(result, 'data.account_type')
    const payload = {
      accountId,
      accountToken,
      expiry,
      cardHolderName,
      cardNickName,
      defaultAccount: false,
      autoPayTime: autoPay ? DEFAULT_AUTOPAY_TIMING : null,
      ...(!isCC && { accountType: accountTypeRaw === 'checking' ? 'Checking' : 'Saving' }),
    }

    addPaymentMethod({ payload })
      .unwrap()
      .then(response => {
        const status = get(response, 'status')
        if (status === 'Added') {
          toast.success(T.PAYMENT_METHOD_ADD_SUCCESS_MESSAGE)
        } else if (status === 'Exists') {
          toast.error(T.PAYMENT_METHOD_ALREADY_EXISTS)
        } else {
          toast.error(status)
        }
        handleClose()
      })
      .catch(handleError)
  }

  const handleFortisError = error => {
    setFortisError('Unable to load payment form. Please try again.')
  }

  const renderAutopaySection = () => (
    <>
      <Box mt={2}>
        <CommonSwitch title={T.AUTOPAY} isChecked={autoPay} onChange={checked => handleChange('autoPay', checked)} />
      </Box>
      {autoPay && <AutopayDisclaimer show={autoPay} />}
    </>
  )

  return (
    <HHBaseDialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth disableScrollLock>
      <HHDialogTitle title={T.ADD_PAYMENT_METHOD} onClose={handleClose} />
      {!isFortis && (
        <RenderPaymentForm
          accountId={accountId}
          isLoading={isLoading}
          type={type}
          onChange={handleChange}
          onChangeBulk={handleChangeBulk}
          formFields={newMethodFields}
          onClose={onClose}
          onSave={handleCardConnectSave}
        />
      )}

      {isFortis && (
        <DialogContent sx={{ width: '100%' }}>
          {fortisError ? (
            <>
              <Alert severity="error" sx={{ mb: 2 }}>
                {fortisError}
              </Alert>
              <Button variant="outlined" fullWidth onClick={handleRetry}>
                Retry
              </Button>
            </>
          ) : !accountId ? (
            <Alert severity="warning">No account ID provided. Please try again.</Alert>
          ) : (
            <>
              <FortisElements
                key={fortisKey}
                accountId={accountId}
                action="tokenization"
                methodType="all"
                SubmitButtonProps={{ fullWidth: true }}
                onPaymentFinished={handleFortisSave}
                onError={handleFortisError}
              />
              {renderAutopaySection()}
            </>
          )}
        </DialogContent>
      )}
    </HHBaseDialog>
  )
}

NewPaymentMethodDialog.propTypes = {
  isOpen: PropTypes.bool,
  accountId: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default NewPaymentMethodDialog
